import type {
  Coverage,
  DirectoryData,
  EntityKind,
  EntityPageState,
  ImplementedLocale,
  MatchCard,
  PageMeta,
  PlayerProfileData,
  PublicFreshness,
  PublicSection,
  TeamProfileData,
} from './types'

import { FootballCoreBack } from '~/components/common/football-core-back'
import {
  footballCoreHref,
  safeFootballCoreBackTarget,
} from '~/lib/navigation/football-core'
import {
  appearanceRoleCopy,
  coverageCaveatCopy,
  entityCopy,
  fixtureStateCopy,
  relationshipKindCopy,
  sectionFreshnessCopy,
  sectionReasonCopy,
  sourceCopy,
  teamTypeCopy,
} from './copy'

const focusClass = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
const actionClass = `inline-flex min-h-11 items-center justify-center rounded-md border border-divider px-4 py-2 text-sm font-semibold text-foreground transition hover:border-primary/60 hover:text-primary ${focusClass}`

function EntityBreadcrumbs({ locale, current, back, fallback }: {
  locale: ImplementedLocale
  current: string
  back?: string
  fallback: string
}) {
  const text = entityCopy(locale)
  return (
    <nav data-breadcrumbs aria-label={text.breadcrumbLabel} className="flex min-w-0 flex-wrap items-center gap-x-2 text-sm text-muted">
      <FootballCoreBack
        className={`inline-flex min-h-11 items-center font-bold text-primary ${focusClass}`}
        explicitTarget={back}
        fallback={fallback}
        locale={locale}
      >
        <span aria-hidden="true">←</span>
        <span className="ml-2">{text.back}</span>
      </FootballCoreBack>
      <span aria-hidden="true">·</span>
      <a className={`inline-flex min-h-11 items-center font-semibold text-foreground ${focusClass}`} href={footballCoreHref(locale, '/teams-players')}>
        {text.teamsPlayers}
      </a>
      <span aria-hidden="true">/</span>
      <span className="min-w-0 break-words" aria-current="page">{current}</span>
    </nav>
  )
}

function formatDate(locale: ImplementedLocale, value: string | null): string {
  if (!value)
    return '—'
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00.000Z`))
}

function formatInstant(locale: ImplementedLocale, value: string | null): string {
  if (!value)
    return '—'
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(new Date(value))
}

function hasStaleFreshness(freshness: PublicFreshness): boolean {
  return freshness.state === 'stale' || freshness.state === 'provider_failed'
}

function LoadingState({ label }: { label: string }) {
  return (
    <div role="status" className="grid gap-3 rounded-xl border border-divider bg-background p-6" aria-live="polite">
      <div className="h-4 w-32 animate-pulse rounded bg-default-200" />
      <div className="h-16 animate-pulse rounded-lg bg-default-100" />
      <span className="sr-only">{label}</span>
      <p className="text-sm text-muted" aria-hidden="true">{label}</p>
    </div>
  )
}

function ErrorState({ locale, kind, onRetry, embedded = false }: {
  locale: ImplementedLocale
  kind: 'team' | 'player' | 'network'
  onRetry: () => void
  embedded?: boolean
}) {
  const text = entityCopy(locale)
  if (kind === 'network') {
    return (
      <section className="rounded-xl border border-danger/30 bg-danger/5 p-6" aria-labelledby="entity-network-error">
        {embedded
          ? <h2 id="entity-network-error" className="text-2xl font-bold text-foreground">{text.networkTitle}</h2>
          : <h1 id="entity-network-error" className="text-2xl font-bold text-foreground">{text.networkTitle}</h1>}
        <p className="mt-2 max-w-2xl text-muted">{text.networkBody}</p>
        <button type="button" className={`${actionClass} mt-5`} onClick={onRetry}>{text.retry}</button>
      </section>
    )
  }
  const title = kind === 'team' ? text.teamNotFound : text.playerNotFound
  return (
    <section className="rounded-xl border border-divider bg-background p-6" aria-labelledby="entity-not-found">
      <h1 id="entity-not-found" className="text-2xl font-bold text-foreground">{title}</h1>
      <p className="mt-2 max-w-2xl text-muted">{text.notFoundBody}</p>
      <a className={`${actionClass} mt-5`} href={footballCoreHref(locale, '/teams-players')}>{text.searchEntities}</a>
    </section>
  )
}

function StaleNotice({ locale }: { locale: ImplementedLocale }) {
  return (
    <p role="status" className="rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm font-medium text-foreground">
      {entityCopy(locale).stale}
    </p>
  )
}

function RefreshErrorNotice({ locale, onRetry }: { locale: ImplementedLocale, onRetry: () => void }) {
  const text = entityCopy(locale)
  return (
    <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3">
      <p className="text-sm font-medium text-foreground">{text.refreshFailed}</p>
      <button type="button" className={actionClass} onClick={onRetry}>{text.retry}</button>
    </div>
  )
}

function SectionUnavailable({ locale, section }: {
  locale: ImplementedLocale
  section: Pick<PublicSection<unknown>, 'freshness' | 'reason'>
}) {
  const fallbackReason = section.freshness.state === 'stale'
    ? 'out_of_date'
    : section.freshness.state === 'provider_failed' ? 'temporarily_unavailable' : 'not_available'
  return (
    <p role="status" className="grid gap-1 rounded-lg border border-divider bg-default-50 p-4 text-sm text-muted">
      <span>{sectionReasonCopy(locale, section.reason ?? fallbackReason)}</span>
      <span>{sectionFreshnessCopy(locale, section.freshness.state)}</span>
    </p>
  )
}

function ProfilePagination({ pagination, previousLabel, nextLabel, hrefForPage }: {
  pagination: PageMeta
  previousLabel: string
  nextLabel: string
  hrefForPage: (page: number) => string
}) {
  if (pagination.page <= 1 && !pagination.hasNextPage)
    return null
  return (
    <nav aria-label={`${pagination.page}/${pagination.totalPages}`} className="flex flex-wrap items-center justify-between gap-3 border-t border-divider pt-4">
      {pagination.page > 1
        ? <a className={actionClass} href={hrefForPage(pagination.page - 1)}>{previousLabel}</a>
        : <span />}
      <span className="text-sm text-muted">
        {pagination.totalPages === 0 ? pagination.page : `${pagination.page} / ${pagination.totalPages}`}
      </span>
      {pagination.hasNextPage
        ? <a className={actionClass} href={hrefForPage(pagination.page + 1)}>{nextLabel}</a>
        : <span />}
    </nav>
  )
}

function CoverageNotice({ locale, coverage }: { locale: ImplementedLocale, coverage: Coverage }) {
  const text = entityCopy(locale)
  const label = coverage.completeness === 'partial'
    ? text.partialCoverage
    : coverage.completeness === 'unknown' ? text.unknownCoverage : text.coverageRange
  return (
    <aside className="grid gap-1 rounded-lg border border-divider bg-default-50 px-4 py-3 text-sm text-muted">
      <p>
        <strong className="text-foreground">{label}</strong>
        <span className="ml-2">
          {text.sample}
          :
          {coverage.sampleSize}
          {' '}
          ·
          {formatDate(locale, coverage.from)}
          {' '}
          –
          {formatDate(locale, coverage.to)}
        </span>
      </p>
      <p>
        {sourceCopy(locale, coverage.source)}
        {' '}
        ·
        {' '}
        {text.projectedAt}
        {' '}
        {formatInstant(locale, coverage.projectedAt)}
      </p>
      {coverage.caveats.length > 0 && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1" aria-label={text.source}>
          {coverage.caveats.map(caveat => <li key={caveat}>{coverageCaveatCopy(locale, caveat)}</li>)}
        </ul>
      )}
    </aside>
  )
}

function MatchSummary({ locale, match, actionLabel, origin }: {
  locale: ImplementedLocale
  match: MatchCard
  actionLabel: string
  origin: string
}) {
  return (
    <article className="grid gap-3 rounded-lg border border-divider bg-background p-4 sm:grid-cols-[1fr_auto] sm:items-center">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted">{match.competition.competitionName}</p>
        <h3 className="mt-1 text-base font-semibold text-foreground">
          {match.homeTeam.name}
          {' '}
          <span className="text-muted">{entityCopy(locale).versus}</span>
          {' '}
          {match.awayTeam.name}
        </h3>
        <p className="mt-1 text-sm text-muted">
          {formatInstant(locale, match.kickoffAt)}
          {' '}
          ·
          {' '}
          {fixtureStateCopy(locale, match.state)}
        </p>
      </div>
      <a className={actionClass} href={footballCoreHref(locale, `/matches/${encodeURIComponent(match.matchId)}`, { back: origin })}>{actionLabel}</a>
    </article>
  )
}

function Pagination({ locale, search, pagination }: {
  locale: ImplementedLocale
  search: { q: string, type: EntityKind, page: number }
  pagination: DirectoryData['pagination']
}) {
  const text = entityCopy(locale)
  if (pagination.page <= 1 && !pagination.hasNextPage)
    return null
  return (
    <nav aria-label={text.page} className="flex flex-wrap items-center justify-between gap-3 border-t border-divider pt-5">
      {pagination.page > 1
        ? <a className={actionClass} href={footballCoreHref(locale, '/teams-players', { query: { type: search.type, q: search.q, page: pagination.page - 1 } })}>{text.previous}</a>
        : <span />}
      <span className="text-sm text-muted">
        {text.page}
        {' '}
        {pagination.totalPages === 0 ? pagination.page : `${pagination.page} / ${pagination.totalPages}`}
      </span>
      {pagination.hasNextPage
        ? <a className={actionClass} href={footballCoreHref(locale, '/teams-players', { query: { type: search.type, q: search.q, page: pagination.page + 1 } })}>{text.next}</a>
        : <span />}
    </nav>
  )
}

export function DirectoryPageView({ locale, search, state, origin, onRetry }: {
  locale: ImplementedLocale
  search: { q: string, type: EntityKind, page: number }
  state: EntityPageState<DirectoryData>
  origin: string
  onRetry: () => void
}) {
  const text = entityCopy(locale)
  const basePath = footballCoreHref(locale, '/teams-players')
  return (
    <div className="mx-auto grid w-full max-w-6xl grid-cols-[minmax(0,1fr)] gap-7 px-4 py-8 sm:px-6 lg:px-8">
      <header className="grid gap-3 border-b border-divider pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">{text.eyebrow}</p>
        <h1 className="text-3xl font-black tracking-tight text-foreground sm:text-4xl">{text.directoryTitle}</h1>
        <p className="max-w-2xl text-base text-muted">{text.directoryLead}</p>
      </header>

      <form action={basePath} method="get" className="grid gap-3 rounded-xl border border-divider bg-background p-4 sm:grid-cols-[1fr_auto] sm:items-end">
        <label className="grid gap-2 text-sm font-semibold text-foreground">
          {text.searchLabel}
          <input
            name="q"
            type="search"
            defaultValue={search.q}
            minLength={2}
            maxLength={100}
            placeholder={text.searchPlaceholder}
            className={`min-h-11 w-full rounded-md border border-divider bg-background px-3 text-base text-foreground ${focusClass}`}
          />
        </label>
        <input type="hidden" name="type" value={search.type} />
        <input type="hidden" name="page" value="1" />
        <button type="submit" className="min-h-11 rounded-md bg-primary px-5 py-2 font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
          {text.searchAction}
        </button>
      </form>

      <nav aria-label={text.directoryTitle} className="flex flex-wrap gap-2">
        {([
          ['all', text.all],
          ['team', text.teams],
          ['player', text.players],
        ] as const).map(([type, label]) => (
          <a
            key={type}
            href={footballCoreHref(locale, '/teams-players', { query: { type, q: search.q, page: 1 } })}
            aria-current={search.type === type ? 'page' : undefined}
            className={`inline-flex min-h-11 items-center rounded-full px-4 text-sm font-semibold ${focusClass} ${search.type === type ? 'bg-primary text-primary-foreground' : 'border border-divider bg-background text-foreground hover:border-primary/60'}`}
          >
            {label}
          </a>
        ))}
      </nav>
      <p className="text-sm text-muted">{text.supportedScope}</p>

      {state.status === 'loading' && <LoadingState label={text.loadingDirectory} />}
      {state.status === 'error' && <ErrorState locale={locale} kind="network" onRetry={onRetry} embedded />}
      {state.status === 'not-found' && <ErrorState locale={locale} kind="network" onRetry={onRetry} embedded />}
      {state.status === 'success' && (
        <>
          {state.isRefreshing && <p role="status" className="text-sm text-muted">{text.updating}</p>}
          {state.refreshError && <RefreshErrorNotice locale={locale} onRetry={onRetry} />}
          {state.data.items.length === 0
            ? (
                <section className="rounded-xl border border-dashed border-divider bg-default-50 p-7 text-center">
                  <h2 className="text-xl font-bold text-foreground">{search.q.trim().length < 2 ? text.searchLabel : text.emptyTitle}</h2>
                  <p className="mx-auto mt-2 max-w-xl text-muted">{search.q.trim().length < 2 ? text.searchPrompt : text.emptyBody}</p>
                  {search.q.trim().length >= 2 && <a className={`${actionClass} mt-5`} href={footballCoreHref(locale, '/teams-players', { query: { type: search.type, q: '', page: 1 } })}>{text.clearSearch}</a>}
                </section>
              )
            : (
                <ul className="grid gap-4" aria-live="polite">
                  {state.data.items.map((item) => {
                    if (item.type === 'team') {
                      return (
                        <li key={`team-${item.team.teamId}`} className="rounded-xl border border-divider bg-background p-5 shadow-sm">
                          <div className="flex flex-wrap items-start justify-between gap-4">
                            <div className="min-w-0">
                              <span data-result-type className="text-xs font-bold uppercase tracking-[0.16em] text-primary">{text.team}</span>
                              <h2 className="mt-1 break-words text-xl font-bold text-foreground">{item.team.name}</h2>
                              <p className="mt-1 text-sm text-muted">{item.supportedCompetitions.map(scope => `${scope.competitionName} ${scope.seasonLabel}`).join(' · ')}</p>
                            </div>
                            <a className={actionClass} href={footballCoreHref(locale, `/teams/${encodeURIComponent(item.team.teamId)}`, { back: origin })}>{text.openTeam}</a>
                          </div>
                          {item.nextOrRecentMatch && (
                            <div className="mt-4 border-t border-divider pt-4">
                              <MatchSummary locale={locale} match={item.nextOrRecentMatch} actionLabel={text.openMatch} origin={origin} />
                            </div>
                          )}
                        </li>
                      )
                    }
                    return (
                      <li key={`player-${item.player.playerId}`} className="rounded-xl border border-divider bg-background p-5 shadow-sm">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div className="min-w-0">
                            <span data-result-type className="text-xs font-bold uppercase tracking-[0.16em] text-primary">{text.player}</span>
                            <h2 className="mt-1 break-words text-xl font-bold text-foreground">{item.player.name}</h2>
                            <p className="mt-1 text-sm text-muted">{item.player.position ?? '—'}</p>
                            {item.latestTeamContext && (
                              <p className="mt-2 text-sm text-muted">
                                {item.latestTeamContext.team.name}
                                {' '}
                                ·
                                {' '}
                                {relationshipKindCopy(locale, item.latestTeamContext.kind)}
                              </p>
                            )}
                          </div>
                          <a className={actionClass} href={footballCoreHref(locale, `/players/${encodeURIComponent(item.player.playerId)}`, { back: origin })}>{text.openPlayer}</a>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
          <Pagination locale={locale} search={search} pagination={state.data.pagination} />
        </>
      )}
    </div>
  )
}

export function TeamProfileView({ locale, state, back, origin, onRetry }: {
  locale: ImplementedLocale
  state: EntityPageState<TeamProfileData>
  back?: string
  origin: string
  onRetry: () => void
}) {
  const text = entityCopy(locale)
  const fallbackBack = footballCoreHref(locale, '/teams-players')
  const resolvedBack = safeFootballCoreBackTarget(back, fallbackBack, locale)
  if (state.status === 'loading') {
    return (
      <div className="mx-auto grid max-w-6xl gap-4 px-4 py-8">
        <EntityBreadcrumbs locale={locale} current={text.loadingTeam} back={back} fallback={fallbackBack} />
        <h1 className="sr-only">{text.loadingTeam}</h1>
        <LoadingState label={text.loadingTeam} />
      </div>
    )
  }
  if (state.status === 'not-found') {
    return (
      <div className="mx-auto grid max-w-6xl gap-4 px-4 py-8">
        <EntityBreadcrumbs locale={locale} current={text.teamNotFound} back={back} fallback={fallbackBack} />
        <ErrorState locale={locale} kind="team" onRetry={onRetry} />
      </div>
    )
  }
  if (state.status === 'error') {
    return (
      <div className="mx-auto grid max-w-6xl gap-4 px-4 py-8">
        <EntityBreadcrumbs locale={locale} current={text.networkTitle} back={back} fallback={fallbackBack} />
        <ErrorState locale={locale} kind="network" onRetry={onRetry} />
      </div>
    )
  }

  const data = state.data
  const stale = hasStaleFreshness(data.recentMatches.freshness) || hasStaleFreshness(data.playerContexts.freshness)
  const comparisonOpponent = data.nextOrLiveMatch === null
    ? null
    : data.nextOrLiveMatch.homeTeam.teamId === data.team.teamId
      ? data.nextOrLiveMatch.awayTeam
      : data.nextOrLiveMatch.homeTeam
  return (
    <div className="mx-auto grid w-full max-w-6xl grid-cols-[minmax(0,1fr)] gap-7 px-4 py-8 sm:px-6 lg:px-8">
      <EntityBreadcrumbs locale={locale} current={data.team.name} back={back} fallback={fallbackBack} />
      <header className="grid gap-4 border-b border-divider pb-6 sm:grid-cols-[1fr_auto] sm:items-end">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">{data.supportedCompetitions.map(scope => scope.competitionName).join(' · ')}</p>
          <h1 className="mt-2 break-words text-3xl font-black tracking-tight text-foreground sm:text-4xl">{data.team.name}</h1>
          <p className="mt-2 text-sm text-muted">
            {data.team.country ?? '—'}
            {' '}
            ·
            {' '}
            {teamTypeCopy(locale, data.team.teamType)}
          </p>
        </div>
      </header>
      <CoverageNotice locale={locale} coverage={data.coverage} />
      {stale && <StaleNotice locale={locale} />}
      {state.isRefreshing && <p role="status" className="text-sm text-muted">{text.updating}</p>}
      {state.refreshError && <RefreshErrorNotice locale={locale} onRetry={onRetry} />}

      <section className="grid gap-3" aria-labelledby="team-current-match">
        <h2 id="team-current-match" className="text-xl font-bold text-foreground">{text.currentMatch}</h2>
        {data.nextOrLiveMatch
          ? <MatchSummary locale={locale} match={data.nextOrLiveMatch} actionLabel={text.openCurrentMatch} origin={origin} />
          : <p className="rounded-lg border border-divider p-4 text-sm text-muted">—</p>}
      </section>

      <section className="grid gap-3" aria-labelledby="team-recent-matches">
        <h2 id="team-recent-matches" className="text-xl font-bold text-foreground">{text.recentMatches}</h2>
        {data.recentMatches.data === null
          ? <SectionUnavailable locale={locale} section={data.recentMatches} />
          : data.recentMatches.data.items.length === 0
            ? <p className="rounded-lg border border-divider p-4 text-sm text-muted">{text.noRecentMatches}</p>
            : data.recentMatches.data.items.map(match => <MatchSummary key={match.matchId} locale={locale} match={match} actionLabel={text.openMatch} origin={origin} />)}
        {data.recentMatches.data?.truncated && <p className="text-sm text-muted">{text.truncated}</p>}
      </section>

      <section className="grid gap-3" aria-labelledby="team-form">
        <h2 id="team-form" className="text-xl font-bold text-foreground">{text.descriptiveForm}</h2>
        {data.form.data === null
          ? <SectionUnavailable locale={locale} section={data.form} />
          : (
              <p className="rounded-lg border border-divider p-4 text-sm text-muted">
                {text.sample}
                :
                {' '}
                {data.form.data.sampleSize}
              </p>
            )}
      </section>

      <section className="grid gap-3" aria-labelledby="team-players">
        <h2 id="team-players" className="text-xl font-bold text-foreground">{text.playerEvidence}</h2>
        {data.playerContexts.data === null
          ? <SectionUnavailable locale={locale} section={data.playerContexts} />
          : (
              <>
                {data.playerContexts.data.items.length === 0
                  ? <p className="rounded-lg border border-divider p-4 text-sm text-muted">{text.noPlayerEvidence}</p>
                  : (
                      <ul className="grid gap-3 sm:grid-cols-2">
                        {data.playerContexts.data.items.map(context => (
                          <li key={`${context.kind}-${context.player.playerId}-${context.observedOn ?? context.validFrom ?? 'unknown'}`} className="rounded-lg border border-divider bg-background p-4">
                            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted">{relationshipKindCopy(locale, context.kind)}</p>
                            <h3 className="mt-1 break-words font-bold text-foreground">{context.player.name}</h3>
                            <a className={`${actionClass} mt-3`} href={footballCoreHref(locale, `/players/${encodeURIComponent(context.player.playerId)}`, { back: origin })}>
                              {text.openPlayer}
                              {' '}
                              {context.player.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                <ProfilePagination
                  pagination={data.playerContexts.data.pagination}
                  previousLabel={text.previousPlayers}
                  nextLabel={text.nextPlayers}
                  hrefForPage={page => footballCoreHref(locale, `/teams/${encodeURIComponent(data.team.teamId)}`, {
                    query: { playerPage: page },
                    back: back ?? resolvedBack,
                  })}
                />
              </>
            )}
      </section>

      <nav aria-label={text.source} className="flex flex-wrap gap-3 border-t border-divider pt-6">
        {data.links.headToHeadAvailable && comparisonOpponent !== null && (
          <a
            className={actionClass}
            href={footballCoreHref(locale, '/teams/compare', {
              query: { teamA: data.team.teamId, teamB: comparisonOpponent.teamId },
              back: origin,
            })}
          >
            {text.compare}
          </a>
        )}
        {data.links.historyAvailable && <a className={actionClass} href={footballCoreHref(locale, `/teams/${encodeURIComponent(data.team.teamId)}/history`, { back: origin })}>{text.history}</a>}
      </nav>
    </div>
  )
}

export function PlayerProfileView({ locale, state, back, origin, onRetry }: {
  locale: ImplementedLocale
  state: EntityPageState<PlayerProfileData>
  back?: string
  origin: string
  onRetry: () => void
}) {
  const text = entityCopy(locale)
  const fallbackBack = footballCoreHref(locale, '/teams-players')
  const resolvedBack = safeFootballCoreBackTarget(back, fallbackBack, locale)
  if (state.status === 'loading') {
    return (
      <div className="mx-auto grid max-w-6xl gap-4 px-4 py-8">
        <EntityBreadcrumbs locale={locale} current={text.loadingPlayer} back={back} fallback={fallbackBack} />
        <h1 className="sr-only">{text.loadingPlayer}</h1>
        <LoadingState label={text.loadingPlayer} />
      </div>
    )
  }
  if (state.status === 'not-found') {
    return (
      <div className="mx-auto grid max-w-6xl gap-4 px-4 py-8">
        <EntityBreadcrumbs locale={locale} current={text.playerNotFound} back={back} fallback={fallbackBack} />
        <ErrorState locale={locale} kind="player" onRetry={onRetry} />
      </div>
    )
  }
  if (state.status === 'error') {
    return (
      <div className="mx-auto grid max-w-6xl gap-4 px-4 py-8">
        <EntityBreadcrumbs locale={locale} current={text.networkTitle} back={back} fallback={fallbackBack} />
        <ErrorState locale={locale} kind="network" onRetry={onRetry} />
      </div>
    )
  }

  const data = state.data
  const stale = hasStaleFreshness(data.appearances.freshness) || hasStaleFreshness(data.teamRelationships.freshness)
  return (
    <div className="mx-auto grid w-full max-w-6xl grid-cols-[minmax(0,1fr)] gap-7 px-4 py-8 sm:px-6 lg:px-8">
      <EntityBreadcrumbs locale={locale} current={data.player.name} back={back} fallback={fallbackBack} />
      <header className="border-b border-divider pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">{data.player.position ?? text.player}</p>
        <h1 className="mt-2 break-words text-3xl font-black tracking-tight text-foreground sm:text-4xl">{data.player.name}</h1>
        <p className="mt-2 text-sm text-muted">
          {data.player.nationality ?? '—'}
          {' '}
          ·
          {' '}
          {text.asOf}
          {' '}
          {formatDate(locale, data.asOfDate)}
        </p>
      </header>
      <CoverageNotice locale={locale} coverage={data.coverage} />
      {stale && <StaleNotice locale={locale} />}
      {state.isRefreshing && <p role="status" className="text-sm text-muted">{text.updating}</p>}
      {state.refreshError && <RefreshErrorNotice locale={locale} onRetry={onRetry} />}

      <form action={footballCoreHref(locale, `/players/${encodeURIComponent(data.player.playerId)}`)} method="get" className="grid min-w-0 gap-3 rounded-lg border border-divider bg-background p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
        <label className="grid min-w-0 gap-2 text-sm font-semibold text-foreground">
          {text.evidenceDate}
          <input
            type="date"
            name="at"
            defaultValue={data.asOfDate}
            className={`min-h-11 min-w-0 w-full rounded-md border border-divider bg-background px-3 text-base text-foreground ${focusClass}`}
          />
        </label>
        <input type="hidden" name="appearancePage" value="1" />
        <input type="hidden" name="back" value={back ?? resolvedBack} />
        <button type="submit" className={`${actionClass} w-full sm:w-auto`}>{text.applyDate}</button>
      </form>

      <section className="grid gap-3" aria-labelledby="player-appearances">
        <h2 id="player-appearances" className="text-xl font-bold text-foreground">{text.appearances}</h2>
        {data.appearances.data === null
          ? <SectionUnavailable locale={locale} section={data.appearances} />
          : (
              <>
                {data.appearances.data.items.length === 0
                  ? <p className="rounded-lg border border-divider p-4 text-sm text-muted">{text.noAppearances}</p>
                  : (
                      <ul className="grid gap-3">
                        {data.appearances.data.items.map(appearance => (
                          <li key={`${appearance.matchId}-${appearance.team.teamId}`} className="grid gap-3 rounded-lg border border-divider bg-background p-4 sm:grid-cols-[1fr_auto] sm:items-center">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted">{text.appearance}</p>
                              <h3 className="mt-1 font-bold text-foreground">
                                {appearance.team.name}
                                {' '}
                                ·
                                {' '}
                                {appearanceRoleCopy(locale, appearance.role)}
                              </h3>
                              <p className="mt-1 text-sm text-muted">
                                {appearance.competition.competitionName}
                                {' '}
                                ·
                                {' '}
                                {formatInstant(locale, appearance.kickoffAt)}
                                {' '}
                                ·
                                {' '}
                                {appearance.opponent.name}
                              </p>
                            </div>
                            <a className={actionClass} href={footballCoreHref(locale, `/matches/${encodeURIComponent(appearance.matchId)}`, { back: origin })}>{text.openAppearance}</a>
                          </li>
                        ))}
                      </ul>
                    )}
                <ProfilePagination
                  pagination={data.appearances.data.pagination}
                  previousLabel={text.previousAppearances}
                  nextLabel={text.nextAppearances}
                  hrefForPage={page => footballCoreHref(locale, `/players/${encodeURIComponent(data.player.playerId)}`, {
                    query: { appearancePage: page, at: data.asOfDate },
                    back: back ?? resolvedBack,
                  })}
                />
              </>
            )}
      </section>

      <section className="grid gap-3" aria-labelledby="player-relationships">
        <h2 id="player-relationships" className="text-xl font-bold text-foreground">{text.relationships}</h2>
        {data.teamRelationships.data === null
          ? <SectionUnavailable locale={locale} section={data.teamRelationships} />
          : data.teamRelationships.data.items.length === 0
            ? <p className="rounded-lg border border-divider p-4 text-sm text-muted">{text.noRelationships}</p>
            : (
                <ul className="grid gap-3 sm:grid-cols-2">
                  {data.teamRelationships.data.items.map((relationship) => {
                    const kindLabel = relationship.kind === 'appearance'
                      ? text.appearance
                      : relationship.kind === 'observation' ? text.observation : text.confirmedMembership
                    const caveat = relationship.boundaryConfidence === 'start_unknown'
                      ? text.startUnknown
                      : relationship.boundaryConfidence === 'end_unknown'
                        ? text.endUnknown
                        : relationship.boundaryConfidence === 'both_unknown' ? text.bothUnknown : null
                    return (
                      <li key={`${relationship.kind}-${relationship.team.teamId}-${relationship.validFrom ?? 'unknown'}-${relationship.validTo ?? 'unknown'}`} className="rounded-lg border border-divider bg-background p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary">{kindLabel}</p>
                        <h3 className="mt-1 break-words font-bold text-foreground">{relationship.team.name}</h3>
                        {relationship.kind === 'confirmed_membership' && (
                          <p className="mt-2 text-sm text-muted">
                            {formatDate(locale, relationship.validFrom)}
                            {' '}
                            –
                            {' '}
                            {formatDate(locale, relationship.validTo)}
                          </p>
                        )}
                        {caveat && <p className="mt-2 text-sm font-medium text-warning-foreground">{caveat}</p>}
                        <a className={`${actionClass} mt-3`} href={footballCoreHref(locale, `/teams/${encodeURIComponent(relationship.team.teamId)}`, { back: origin })}>{text.openRelationshipTeam}</a>
                      </li>
                    )
                  })}
                </ul>
              )}
        {data.teamRelationships.data?.truncated && <p className="text-sm text-muted">{text.truncated}</p>}
      </section>

      <section className="grid gap-3" aria-labelledby="player-statistics">
        <h2 id="player-statistics" className="text-xl font-bold text-foreground">{text.statistics}</h2>
        {data.historicalStatistics.data === null
          ? <SectionUnavailable locale={locale} section={data.historicalStatistics} />
          : data.historicalStatistics.data.items.length === 0
            ? <p className="rounded-lg border border-divider p-4 text-sm text-muted">{text.noStatistics}</p>
            : (
                <p className="rounded-lg border border-divider p-4 text-sm text-muted">
                  {text.sample}
                  :
                  {' '}
                  {data.historicalStatistics.data.items.length}
                </p>
              )}
      </section>
    </div>
  )
}
